import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { useGetSmsAlertsPerDayQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from './TimeRangeDropdown';
import { formatBucketTick, formatBucketLabel } from '../utils/formatBucket';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

const SMS_COLOR = '#C769EB';

const SmsAlertsChart: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error, refetch } = useGetSmsAlertsPerDayQuery({ period });

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>SMS Alerts Sent</h2>
          <p className="chart-subtitle">Have-list SMS alerts successfully sent (UTC)</p>
        </div>
        <div className="chart-header-right">
          <div className="chart-legend">
            <span className="legend-dot sms-alerts" />
            <span className="legend-label">Alerts</span>
          </div>
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">
            Failed to load data
            <button className="chart-retry-btn" onClick={() => refetch()}>Retry</button>
          </div>
        ) : data ? (
          <div className="chart-empty-wrap">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatBucketTick}
                  stroke="#666"
                  tick={{ fill: '#999', fontSize: 12 }}
                  axisLine={{ stroke: '#2a2a2a' }}
                  tickLine={false}
                  interval={data.data.length > 60 ? Math.floor(data.data.length / 12) : 0}
                />
                <YAxis
                  stroke="#666"
                  tick={{ fill: '#999', fontSize: 12 }}
                  axisLine={{ stroke: '#2a2a2a' }}
                  tickLine={false}
                  allowDecimals={false}
                  label={{
                    value: 'SMS Alerts Sent',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#999', fontSize: 12, textAnchor: 'middle' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #2a2a2a',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                  labelFormatter={(label) => formatBucketLabel(label as string, period)}
                  formatter={(value) => [`${Number(value).toLocaleString()}`, 'Alerts sent']}
                />
                <Bar
                  dataKey="count"
                  fill={SMS_COLOR}
                  radius={[4, 4, 0, 0]}
                  name="SMS Alerts Sent"
                />
              </BarChart>
            </ResponsiveContainer>
            {data.totalSmsSent === 0 && (
              <div className="chart-empty">No SMS alerts sent in this period</div>
            )}
          </div>
        ) : null}
      </div>

      {data && (
        <div className="chart-summary">
          Total: {data.totalSmsSent.toLocaleString()} SMS alerts sent in the last {period === 7 ? 'week' : period === 30 ? 'month' : 'year'}
        </div>
      )}
    </div>
  );
};

export default SmsAlertsChart;
